import {
  GG_HOVERS,
  GG_HOVERS_DEF,
  SCREENING_SECTIONS,
  MDS_COMMUNICATION_FORM,
  otherKeyFor,
  type ScreeningField,
  type ScreeningSection,
} from './screeningFields';

// JSON Forms UI schema for the screening form, generated from SCREENING_SECTIONS.
// Each section becomes a Reform CollapsibleSection; the sections share one collapseGroup
// so they behave as an accordion (one open at a time), with the first section open by default.

const COLLAPSE_GROUP = 'screening';

// Screening Type drives section visibility (see the Jotform conditions mirrored in
// ScreeningSection.showWhen).
const SCREENING_TYPE_SCOPE = '#/properties/basicDetails/properties/screeningType';

const ruleForSection = (section: ScreeningSection) => {
  if (!section.showWhen) return undefined;
  const schema =
    section.showWhen === 'mdsOnly'
      ? { const: MDS_COMMUNICATION_FORM }
      : { not: { const: MDS_COMMUNICATION_FORM } };
  return { effect: 'SHOW', condition: { scope: SCREENING_TYPE_SCOPE, schema } };
};

const controlForField = (sectionId: string, field: ScreeningField) => {
  const scope = `#/properties/${sectionId}/properties/${field.key}`;
  const base = { type: 'Control', scope, label: field.label };

  switch (field.control) {
    case 'radio':
      // singleColumn keeps the options stacked vertically (Yes / No / Other).
      return { ...base, options: { format: 'radio', singleColumn: true } };
    case 'buttonRow':
      return {
        ...base,
        options: {
          customRenderer: 'ButtonRow',
          ...(field.optionHoversDef
            ? { titleOverrides: { $ref: `#/$defs/${field.optionHoversDef}` } }
            : {}),
        },
      };
    case 'checkboxRow':
      // Real MUI checkboxes (Checkbox renderer) rather than chip-style CheckboxButtonRow, to
      // match the radio styling. singleColumn stacks them vertically.
      return { ...base, options: { format: 'checkbox', singleColumn: true } };
    case 'date':
      return {
        ...base,
        options: { customRenderer: 'CustomDatePicker', maxDate: { source: 'TODAY' } },
      };
    case 'textarea':
      return { ...base, options: { multi: true } };
    case 'text':
    default:
      return base;
  }
};

const SPACER = { type: 'Layout', options: { customRenderer: 'Spacer' } };
const DIVIDER = { type: 'Layout', options: { customRenderer: 'Divider' } };

// Free-text control revealed when an "Other" option is selected, mirroring the Jotform
// radio "other" input. Rendered beside its parent field (see flushRadioRun).
const otherControlForField = (sectionId: string, field: ScreeningField) => ({
  type: 'Control',
  scope: `#/properties/${sectionId}/properties/${otherKeyFor(field.key)}`,
  label: `${field.label} — Other`,
  rule: {
    effect: 'SHOW',
    condition: {
      scope: `#/properties/${sectionId}/properties/${field.key}`,
      schema: { const: 'Other' },
    },
  },
});

const buildFieldElements = (sectionId: string, fields: ScreeningField[]) => {
  const elements: unknown[] = [];
  let buttonRun: ScreeningField[] = [];
  let radioRun: ScreeningField[] = [];
  let rowRun: ScreeningField[] = [];

  // Consecutive buttonRow fields share one ButtonRowGroup (aligned label/options grid). A run whose
  // fields declare a `group` becomes a card titled with that category (as the evaluations do inside
  // an Objective Assessment tab); an ungrouped run stays frameless, i.e. a bare grid.
  const flushButtonRun = () => {
    if (!buttonRun.length) return;
    const { group } = buttonRun[0];
    elements.push({
      type: 'Layout',
      ...(group ? { title: group } : { frameless: true }),
      elements: buttonRun.map((f) => controlForField(sectionId, f)),
      options: { customRenderer: 'ButtonRowGroup' },
    });
    buttonRun = [];
  };

  // Consecutive simple radio questions are laid out two-per-row via HorizontalLayout. A field with
  // an "Other" option takes a full row so its conditional free-text can sit directly beneath it.
  const flushRadioRun = () => {
    if (!radioRun.length) return;
    let pair: ScreeningField[] = [];
    const emitPair = () => {
      if (!pair.length) return;
      elements.push({
        type: 'HorizontalLayout',
        elements: pair.map((f) => controlForField(sectionId, f)),
      });
      pair = [];
    };
    radioRun.forEach((field) => {
      if (field.allowOther) {
        emitPair();
        // Field on the left, its conditional "Other" free-text to the right.
        elements.push({
          type: 'HorizontalLayout',
          elements: [controlForField(sectionId, field), otherControlForField(sectionId, field)],
        });
      } else {
        pair.push(field);
        if (pair.length === 2) emitPair();
      }
    });
    emitPair();
    radioRun = [];
  };

  // Plain controls tagged with the same `row` share one HorizontalLayout, so they sit side by side
  // instead of each taking a full-width line.
  const flushRowRun = () => {
    if (!rowRun.length) return;
    const controls = rowRun.map((f) => controlForField(sectionId, f));
    elements.push(
      controls.length === 1 ? controls[0] : { type: 'HorizontalLayout', elements: controls }
    );
    rowRun = [];
  };

  const flushAll = () => {
    flushButtonRun();
    flushRadioRun();
    flushRowRun();
  };

  fields.forEach((field) => {
    if (field.control === 'radio') {
      flushButtonRun();
      flushRowRun();
      radioRun.push(field);
    } else if (field.control === 'buttonRow') {
      flushRadioRun();
      flushRowRun();
      // A change of category closes the open card and starts the next one.
      if (buttonRun.length && buttonRun[0].group !== field.group) flushButtonRun();
      buttonRun.push(field);
    } else if (field.row) {
      flushButtonRun();
      flushRadioRun();
      // A change of row closes the open line and starts the next one.
      if (rowRun.length && rowRun[0].row !== field.row) flushRowRun();
      rowRun.push(field);
    } else {
      flushAll();
      elements.push(controlForField(sectionId, field));
    }
    // Flush any open run so the spacer / divider lands directly after this field.
    if (field.spacerAfter) {
      flushAll();
      const rows = field.spacerAfter === true ? 1 : field.spacerAfter;
      for (let i = 0; i < rows; i += 1) elements.push({ ...SPACER });
    }
    if (field.dividerAfter) {
      flushAll();
      elements.push({ ...DIVIDER });
    }
  });
  flushAll();

  return elements;
};

const sectionElement = (section: ScreeningSection, index: number) => {
  const elements: unknown[] = [];

  if (section.instructions) {
    elements.push({
      type: 'Layout',
      options: { customRenderer: 'Text' },
      content: section.instructions,
    });
  }

  elements.push(...buildFieldElements(section.id, section.fields));

  const rule = ruleForSection(section);

  return {
    type: 'Layout',
    label: section.title,
    collapseGroup: COLLAPSE_GROUP,
    groupId: section.id,
    defaultExpanded: index === 0,
    elements,
    ...(rule ? { rule } : {}),
    options: { customRenderer: 'CollapsibleSection' },
  };
};

export const uischema = {
  type: 'VerticalLayout',
  // Reform's ButtonRow resolves `titleOverrides` against the root UI schema's `$defs`.
  $defs: { [GG_HOVERS_DEF]: GG_HOVERS },
  elements: SCREENING_SECTIONS.map(sectionElement),
};
